/**
 * Pure geometry helper for camera capture.
 *
 * Root-cause fix for the shipping box-picture distortion: capture code used to draw the
 * camera frame into a hard-coded canvas (e.g. 1920x1080) with `drawImage(video, 0, 0, w, h)`,
 * which stretches any frame whose aspect ratio differs from the canvas. A 4:3 phone frame
 * forced into 16:9 is exactly the horizontal stretch seen in the reported image.
 *
 * This helper sizes the canvas to the frame's real aspect ratio so the capture is never stretched.
 *
 * Note on orientation: we deliberately do NOT rotate based on `screen.orientation`. That angle is
 * measured from the device's *natural* orientation (landscape on desktops, portrait on phones), so
 * it can't tell us the frame's content orientation — a first attempt rotated desktop webcam captures
 * 90°. Modern browsers already deliver correctly-oriented frames (auto-oriented on phones, native
 * landscape on desktop), so trusting `videoWidth/videoHeight` is both simpler and correct.
 */
export interface CaptureGeometry {
  /** Canvas pixel width — matches the frame's intrinsic width. */
  canvasWidth: number;
  /** Canvas pixel height — matches the frame's intrinsic height. */
  canvasHeight: number;
}

/**
 * @param videoWidth  intrinsic frame width  (`HTMLVideoElement.videoWidth`)
 * @param videoHeight intrinsic frame height (`HTMLVideoElement.videoHeight`)
 */
export function computeCaptureGeometry(videoWidth: number, videoHeight: number): CaptureGeometry {
  // Always preserve the frame's real aspect ratio — never scale to a fixed resolution.
  return {
    canvasWidth: videoWidth,
    canvasHeight: videoHeight,
  };
}
